// @ts-nocheck
/*
 * Port of utilities/dispositionFormsAcl.js (162 lines). BYTE-IDENTICAL.
 * Two orthogonal disposition-forms ACL sub-options tracked in the module
 * `edit_options` list: `custom_timing` (wrap-up duration override) and
 * `allow_followups` (schedule a follow-up on submission). SUPERADMIN
 * short-circuits every check. `edit_options` missing → treated as "all
 * granted" for back-compat.
 */
import { MODULE_ACL } from './constant';
import { isAclModuleOptionsRelevant } from './aclModuleOptionsAccess';
import { parseModuleOperations } from './voicePlanAcl';

export const DISPOSITION_FORMS_ACL_MODULE = MODULE_ACL.DISPOSITION_FORMS;
export const CUSTOM_TIMING_ACL_OPTION = 'custom_timing';
export const ALLOW_FOLLOWUPS_ACL_OPTION = 'allow_followups';
// Sub-option of allow_followups: agents may get "call follow-ups before their time"
// (per agent, agents.allow_early_follow_up). Never kept without allow_followups.
export const EARLY_FOLLOW_UP_ACL_OPTION = 'early_follow_up';

export const DISPOSITION_TIMING_OPTION_DEFS = [
  {
    value: CUSTOM_TIMING_ACL_OPTION,
    label: 'Custom timing',
    description: 'Allow configuring a custom wrap-up duration for disposition forms',
  },
  {
    value: ALLOW_FOLLOWUPS_ACL_OPTION,
    defaultOn: false,
    label: 'Allow follow-ups',
    description: 'Allow enabling follow-up scheduling on disposition forms',
  },
  {
    value: EARLY_FOLLOW_UP_ACL_OPTION,
    defaultOn: false,
    label: 'Early follow-up calls',
    description: 'Allow agents to be given early follow-up calls (before the scheduled time)',
  },
];

export const DEFAULT_DISPOSITION_TIMING_OPTIONS = [CUSTOM_TIMING_ACL_OPTION];

const VALID_DISPOSITION_TIMING_OPTIONS = [
  CUSTOM_TIMING_ACL_OPTION,
  ALLOW_FOLLOWUPS_ACL_OPTION,
  EARLY_FOLLOW_UP_ACL_OPTION,
];

export function getDispositionFormsAclEntry(moduleOperations) {
  const ops = parseModuleOperations(moduleOperations);
  return ops[DISPOSITION_FORMS_ACL_MODULE] || null;
}

export function normalizeDispositionTimingOptions(options) {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => VALID_DISPOSITION_TIMING_OPTIONS.includes(o)),
    ),
  ];
}

export function getParentGrantableDispositionTimingOptions(moduleOperations, isSuperAdmin) {
  if (isSuperAdmin) {
    return [...VALID_DISPOSITION_TIMING_OPTIONS];
  }

  const entry = getDispositionFormsAclEntry(moduleOperations);
  if (!entry?.add && !entry?.edit && !entry?.read && !entry?.view_permission) {
    return [];
  }

  if (!Array.isArray(entry?.edit_options)) {
    return [...VALID_DISPOSITION_TIMING_OPTIONS];
  }

  return normalizeDispositionTimingOptions(entry.edit_options);
}

export function getDispositionTimingOptionsForEditor(moduleOperations, isSuperAdmin) {
  const grantable = getParentGrantableDispositionTimingOptions(moduleOperations, isSuperAdmin);
  return DISPOSITION_TIMING_OPTION_DEFS.filter((opt) => grantable.includes(opt.value));
}

export function normalizeDispositionTimingOptionsForSave(options, parentGrantableOptions = []) {
  let normalized = normalizeDispositionTimingOptions(options);
  if (Array.isArray(parentGrantableOptions) && parentGrantableOptions.length > 0) {
    const allowed = new Set(parentGrantableOptions);
    normalized = normalized.filter((opt) => allowed.has(opt));
  }
  // Early follow-up calls only exist on top of Allow follow-ups.
  if (!normalized.includes(ALLOW_FOLLOWUPS_ACL_OPTION)) {
    normalized = normalized.filter((opt) => opt !== EARLY_FOLLOW_UP_ACL_OPTION);
  }
  return normalized;
}

/**
 * Agent create/edit "Early Follow-up Call" toggle (agents.allow_early_follow_up): super admin,
 * or a Disposition Forms ACL that lists both "allow_followups" and "early_follow_up".
 * Opt-in: no "nothing configured -> allowed" fallback.
 */
export function canUseEarlyFollowUp(moduleOperations, { isSuperAdmin = false } = {}) {
  if (isSuperAdmin) return true;
  const entry = getDispositionFormsAclEntry(moduleOperations);
  if (!isDispositionFormsModuleAccessible(entry) || !Array.isArray(entry?.edit_options)) return false;
  const options = normalizeDispositionTimingOptions(entry.edit_options);
  return options.includes(ALLOW_FOLLOWUPS_ACL_OPTION) && options.includes(EARLY_FOLLOW_UP_ACL_OPTION);
}

export function formatDispositionTimingAclLabel(moduleEntry) {
  if (!isAclModuleOptionsRelevant(moduleEntry)) return '—';
  const options = normalizeDispositionTimingOptions(moduleEntry.edit_options);
  const labels = DISPOSITION_TIMING_OPTION_DEFS.filter((opt) => options.includes(opt.value)).map(
    (opt) => opt.label,
  );
  return labels.length ? labels.join(', ') : '—';
}

export function hasDispositionCustomTimingAcl(moduleOperations, { isSuperAdmin = false } = {}) {
  if (isSuperAdmin) return true;

  const entry = getDispositionFormsAclEntry(moduleOperations);
  if (!entry?.add && !entry?.edit) {
    return false;
  }

  if (!Array.isArray(entry.edit_options)) {
    return true;
  }

  return normalizeDispositionTimingOptions(entry.edit_options).includes(
    CUSTOM_TIMING_ACL_OPTION,
  );
}

export function isDispositionFormsModuleAccessible(moduleEntry) {
  if (!moduleEntry || typeof moduleEntry !== 'object') return false;
  return ['read', 'add', 'edit', 'delete', 'view_permission'].some(
    (op) => moduleEntry[op] === true,
  );
}

export function canUseDispositionFollowUpsFeature(
  moduleOperations,
  { isSuperAdmin = false } = {},
) {
  if (isSuperAdmin) return true;

  const entry = getDispositionFormsAclEntry(moduleOperations);
  if (!isDispositionFormsModuleAccessible(entry)) {
    return false;
  }

  if (!Array.isArray(entry.edit_options)) {
    return true;
  }

  return normalizeDispositionTimingOptions(entry.edit_options).includes(
    ALLOW_FOLLOWUPS_ACL_OPTION,
  );
}

export function hasDispositionAllowFollowUpsAcl(
  moduleOperations,
  { isSuperAdmin = false } = {},
) {
  if (isSuperAdmin) return true;

  const entry = getDispositionFormsAclEntry(moduleOperations);
  if (!entry?.add && !entry?.edit) {
    return false;
  }

  if (!Array.isArray(entry.edit_options)) {
    return true;
  }

  return normalizeDispositionTimingOptions(entry.edit_options).includes(
    ALLOW_FOLLOWUPS_ACL_OPTION,
  );
}
